<template>
  <mouse-layout :can-move-to-list="true" :loading="loading">
    <div class="contents">
      <!-- -------------------------------------------------------- -->
      <!-- 分析条件 -->
      <!-- -------------------------------------------------------- -->
      <div class="filter">
        <query-filter-element id="basic" title="基本条件" contentWidth="medium">
          <div class="company">
            <div class="title">
              <gl-sub-text>対象企業</gl-sub-text>
            </div>
            <gl-select-company :company.sync="queryFilter.basic.company" />
          </div>

          <filter-basic
            v-bind.sync="queryFilter.basic"
            :available-data-types="availableDataTypes"
          />
        </query-filter-element>

        <!-- 期間 -->
        <query-filter-element id="term" title="期間" contentWidth="medium">
          <filter-term v-bind.sync="queryFilter.term" />
        </query-filter-element>

        <!-- 商品分類 -->
        <query-filter-element id="item" title="商品分類">
          <div>
            <md-radio v-model="queryFilter.item.selectMode" value="category">
              分類
              <gl-sub-text>で指定</gl-sub-text>
            </md-radio>
            <md-radio v-model="queryFilter.item.selectMode" value="item">
              商品
              <gl-sub-text>で指定</gl-sub-text>
            </md-radio>
          </div>
          <filter-item-category
            v-if="queryFilter.item.selectMode === 'category'"
            v-bind.sync="queryFilter.item"
            :company.sync="queryFilter.basic.company"
            :availableItemCategory="availableItemCategory"
          />
          <filter-item
            v-if="queryFilter.item.selectMode === 'item'"
            v-bind.sync="queryFilter.item"
            :company.sync="queryFilter.basic.company"
            :availableItemCategory="availableItemCategory"
          />
        </query-filter-element>

        <!-- 店舗 -->
        <query-filter-element id="store" title="店舗" contentWidth="medium">
          <filter-store
            v-bind.sync="queryFilter.store"
            :company.sync="queryFilter.basic.company"
          />
        </query-filter-element>

        <!-- 顧客-->
        <query-filter-element
          id="customer"
          title="顧客"
          :disabled="disabledFilterCustomer"
        >
          <filter-customer
            v-bind.sync="queryFilter.customer"
            :disabled="disabledFilterCustomer"
          />
        </query-filter-element>
      </div>
      <div v-if="this.hasInvalidInput" class="fixed-error-message-area">
        <gl-error-text :errors="this.invalidMessages" />
      </div>
      <transition name="query-button">
        <div
          class="fixed-action-area"
          :gl-tooltip="buttonToolTip"
          gl-tooltip-direction="left"
        >
          <button-execute-query
            @click="executeQuery"
            class="animated"
            :disabled="!canStartQuery"
            :will-plane-fly="canStartQuery"
            :class="{ tada: canStartQuery }"
          />
        </div>
      </transition>
    </div>

    <!-- 顧客ファイルアップロード中通知 -->
    <md-dialog
      :md-active.sync="uploadingCustomerFile"
      :md-close-on-esc="false"
      :md-click-outside-to-close="false"
    >
      <md-dialog-content>
        <div style="display:flex;align-items:center">
          <spinner :diameter="30" />
          <span style="margin-left:var(--spacing-medium)">
            顧客一覧ファイルをアップロードしています...
          </span>
        </div>
      </md-dialog-content>
    </md-dialog>

    <md-dialog-alert
      :md-active.sync="hasCustomerFileUploadWarning"
      :md-content="customerFileUploadWarningMessage"
      md-confirm-text="Close"
    />

    <!-- 分析条件復元失敗エラーダイアログ -->
    <md-dialog-alert
      :md-active.sync="showFailedToApplyQueryFilter"
      md-content="分析条件の読込に失敗しました。保存されていた分析条件は削除されます。"
      md-confirm-text="Close"
    />

    <md-snackbar
      md-position="center"
      :md-duration="snackbar.duration"
      :md-active.sync="snackbar.show"
    >
      <span>{{ snackbar.message }}</span>
    </md-snackbar>
  </mouse-layout>
</template>

<script>
// 分析条件, 分析結果のためのレイアウト
import MouseLayout from '@/components/layout/MouseLayout.vue'
// 分析条件サマリ
// 分析条件
import QueryFilterElement from '@/components/QueryFilterElement.vue'
import FilterBasic from '@/components/FilterBasic.vue'
import FilterTerm from '@/components/FilterTerm.vue'
import FilterItemCategory from '@/components/FilterItemCategory.vue'
import FilterItem from '@/components/FilterItem.vue'
import FilterStore from '@/components/FilterStore.vue'
import FilterCustomer from '@/components/FilterCustomer.vue'
// 分析条件モデル
import basic from '@/queryfilter/basic'
import term from '@/queryfilter/term'
import item from '@/queryfilter/item'
import store from '@/queryfilter/store'
import customer from '@/queryfilter/customer'
import ButtonExecuteQuery from '@/components/ButtonExecuteQuery'
import Spinner from '@/components/Spinner.vue'

export default {
  components: {
    MouseLayout,
    QueryFilterElement,
    FilterBasic,
    FilterTerm,
    FilterItem,
    FilterItemCategory,
    FilterStore,
    FilterCustomer,
    Spinner,
    ButtonExecuteQuery,
  },
  data() {
    return {
      loading: false,

      /**
       * 分析条件
       */
      queryFilter: {
        basic: { ...basic.get(this.$store.state.queryMetadata.outputColumns) },
        term: { ...term.get() },
        item: { ...item.get() },
        store: { ...store.get() },
        customer: { ...customer.get() },
      },
      /**
       * この画面が持つ分析条件を、クエリのペイロードに変換する
       */
      mapQueryFilterForApi: queryFilter => ({
        company: queryFilter.basic.company,
        description: queryFilter.basic.description,
        type: queryFilter.basic.type,
        outputColumns: queryFilter.basic.outputColumns,
        optionalDownloads: queryFilter.basic.optionalDownloads,
        term: term.mapToApiPayload(queryFilter.term),
        item: item.mapToApiPayload(queryFilter.item),
        store: store.mapToApiPayload(queryFilter.store),
        customer: customer.mapToApiPayload(queryFilter.customer),
      }),
      /**
       * 渡された分析条件をこの画面の分析条件に反映する
       *
       * 渡される分析条件はこの画面のものとは限らないので、"可能な限り"復元する。
       */
      applyQueryFilter: queryFilter => {
        this.queryFilter.basic.description = queryFilter.description
        this.queryFilter.basic.company = queryFilter.company
        this.queryFilter.basic.type = queryFilter.type
        this.queryFilter.basic.outputColumns =
          queryFilter.outputColumns || this.defaultOutputColumns(queryFilter)
        this.queryFilter.basic.optionalDownloads = queryFilter.optionalDownloads
        term.applyPayload(this.queryFilter.term, queryFilter.term)
        item.applyPayload(
          this.queryFilter.item,
          queryFilter.item,
          queryFilter.company,
        )
        store.applyPayload(this.queryFilter.store, queryFilter.store)
        customer.applyPayload(this.queryFilter.customer, {
          ...queryFilter.customer,
          customerGroupId: undefined, // 一時顧客グループは既に削除されている可能性があるので復元しない
        })

        if (queryFilter.customer.customerGroupId) {
          this.snackbar.message =
            '前回アップロードされた顧客一覧ファイルは復元されません。同じ顧客条件でデータ作成を行う場合は再度アップロードしてください。'
          this.snackbar.show = true
        }
      },
      /**
       * 顧客一覧ファイルのアップロード中フラグ
       */
      uploadingCustomerFile: false,
      /**
       * 顧客一覧ファイルアップロード時の警告有無
       */
      hasCustomerFileUploadWarning: false,
      /**
       * 顧客一覧ファイルアップロード時の警告メッセージ
       *
       * ファイルフォーマット不正や結果0件
       */
      customerFileUploadWarningMessage: undefined,
      /**
       * 分析条件の復元失敗エラーダイアログの開閉状態
       */
      showFailedToApplyQueryFilter: false,

      /**
       * 使用可能マウス条件
       */
      mouseAccounts: undefined,

      /**
       * 使用可能データ種別
       */
      availableDataTypes: undefined,

      snackbar: {
        show: false,
        message: '',
        duration: 5000,
      },
    }
  },
  methods: {
    async executeQuery() {
      // 顧客ファイルの指定がある場合、そのままクエリAPIには送信できないので、
      // 一旦アップロードして一時顧客グループIDを取得してから分析に進む。
      if (
        this.queryFilter.basic.type === 'idpos' &&
        this.queryFilter.customer &&
        !this.queryFilter.customer.customerGroupId &&
        this.queryFilter.customer.file
      ) {
        const customerGroup = await this.uploadCustomerFile(
          this.queryFilter.customer.file,
        )

        if (customerGroup.invalidFile) {
          this.hasCustomerFileUploadWarning = true
          this.customerFileUploadWarningMessage =
            '顧客一覧ファイルが読み込めませんでした。'
          return
        }

        if (customerGroup.numRows === 0) {
          this.hasCustomerFileUploadWarning = true
          this.customerFileUploadWarningMessage =
            '顧客一覧ファイルに有効な顧客が存在しませんでした。'
          return
        }

        this.queryFilter.customer.customerGroupId =
          customerGroup.customerGroupId
      }

      await this.$longQuery.executeQuery(
        'mouse',
        this.mapQueryFilterForApi(this.queryFilter),
      )

      //結果は待たず、一覧へ戻る
      this.$router.push({ name: 'main' })
    },
    /**
     * 顧客一覧ファイルをアップロードして一時顧客グループを作成する
     */
    async uploadCustomerFile(file) {
      this.uploadingCustomerFile = true
      const customerGroup = await this.$customerGroup.upload(file)
      this.uploadingCustomerFile = false

      return customerGroup
    },
    openHelp() {
      window.open(`${process.env.VUE_APP_HELP_SITE_URL_BASE}mouse.html`)
    },
    moveToList() {
      this.$router.push({ name: 'main' })
    },
    defaultOutputColumns(queryFilter) {
      if (!this.$store.state.queryMetadata.outputColumns || !queryFilter) {
        return []
      }
      return this.$store.state.queryMetadata.outputColumns
        .filter(
          f =>
            ((queryFilter.type === 'idpos' && f.idpos) ||
              (queryFilter.type === 'pos' && f.pos)) &&
            f.isDefault,
        )
        .map(m => m.key)
    },
  },
  computed: {
    /**
     * 顧客指定できるか
     */
    disabledFilterCustomer() {
      return this.queryFilter.basic.type === 'pos'
    },

    /**
     * クエリの発行ができる状態か否か
     */
    canStartQuery() {
      return !this.hasInvalidInput
    },
    /**
     * 分析ボタンツールチップ
     */
    buttonToolTip() {
      if (this.hasInvalidInput) {
        return '分析条件に入力エラーがあります。'
      }
      return ''
    },
    hasInvalidInput() {
      return (
        Object.entries(this.queryFilter)
          .map(e => e[1])
          .map(e => (e['errors'] ? e.errors : []))
          .filter(e => e.length > 0).length > 0
      )
    },
    invalidMessages() {
      const messages = Object.entries(this.queryFilter)
        .map(e => e[1])
        .map(e => (e['errors'] ? e.errors : []))
        .filter(e => e.length > 0)
        .map(e => e[0])
      return messages
    },
    availableItemCategory() {
      if (this.mouseAccounts) {
        const mouseAccount = this.mouseAccounts.find(
          c => c.companyCode === this.queryFilter.basic.company,
        )
        return mouseAccount
      }
      return undefined
    },
  },
  watch: {
    '$route.params.requestId': {
      immediate: true,
      async handler(requestId) {
        this.mouseAccounts = await this.$repositories
          .get('mouseAccount')
          .get(this.$store.state.account.userId)

        //どの企業も同じデータ種別が設定されている
        this.availableDataTypes = this.mouseAccounts[0].dataTypes

        if (requestId) {
          this.loading = true
          const queryFilter = await this.$repositories
            .get('downloadRequest')
            .getQueryFilter(requestId)

          const promises = this.applyQueryFilter(queryFilter)
          if (promises && promises.length > 0) {
            await Promise.all(promises.filter(p => p))
          }

          this.loading = false
        } else {
          this.queryFilter.basic.company = this.$store.state.companies[0].code
          this.queryFilter.basic.type = this.availableDataTypes.includes(
            this.queryFilter.basic.type,
          )
            ? this.queryFilter.basic.type
            : this.availableDataTypes[0]
        }
      },
    },
  },
}
</script>

<style lang="scss" scoped>
$header-height: 72px;

.title {
  margin-top: var(--spacing-medium);
}
.contents {
  padding-bottom: var(--spacing-large);
  padding-left: var(--spacing-large);
  padding-right: var(--spacing-large);
  margin-top: var(--spacing-large);
  width: 100%;
}
main {
  display: flex;
  height: 100vh;
  position: relative;
}

.header {
  background: #fff;
  height: $header-height;
  top: 0;
  left: 0;
  right: 0;
  padding-right: var(--spacing-large);
  opacity: 0.9;
  position: fixed;
  z-index: 11;

  display: flex;
  align-items: center;

  .align-left {
    flex: 1;
    margin-left: var(--spacing-large);
  }
}

.header-actions {
  display: flex;
  align-items: center;
}

.filter {
  // 分析条件内に描画される分析条件パネルの表示位置はここで制御します。
  .query-filter-element {
    margin-top: var(--spacing-large);
    &:first-child {
      margin-top: 0;
    }
    &:last-child {
      margin-bottom: var(--spacing-large);
    }
  }
}

.fixed-action-area {
  position: fixed;
  bottom: var(--spacing-medium);
  right: var(--spacing-medium);
  z-index: 11;
}

.fixed-error-message-area {
  background-color: var(--bg-color-primary);
  border: 1px solid var(--color-border);
  position: fixed;
  bottom: calc(var(--spacing-medium) + 48px);
  right: var(--spacing-medium);
  padding-left: var(--spacing-medium);
  padding-right: var(--spacing-medium);
  margin-bottom: var(--spacing-medium);
  border-radius: 5px;
  z-index: 12;
}

.slide-in-enter-active,
.slide-in-leave-active {
  transition: opacity 0.2s ease-in-out, transform 0.2s ease-in-out;
}
.slide-in-enter,
.slide-in-leave-to {
  opacity: 0;
  transform: translateX(100%);
}
.query-button-enter-active,
.query-button-leave-active {
  transition: opacity 0.2s ease-in-out, transform 0.2s ease-in-out;
}
.query-button-enter,
.query-button-leave-to {
  opacity: 0;
  transform: translateX(100%);
}
</style>
